// pages/Screening.js
// ─────────────────────────────────────────────────────────────────────────────
// Pagina principală de analiză FEUR.
// Suportă: freemium (public, 3/zi per IP) și tiers paid (autentificat).
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import Navbar from "../components/Navbar";

const API = process.env.REACT_APP_API_URL || "https://api.narratiq.mybestseller.ro";

// ─────────────────────────────────────────────────────────────────────────────
// TIERS — single source of truth pentru frontend
// Ordinea contează: primul tier accesibil va fi selectat implicit.
// ─────────────────────────────────────────────────────────────────────────────
const TIERS = [
  { id: "freemium",            label: "Gratuit — Hook Preview",            words: 2000  },
  { id: "basic",               label: "Basic",                             words: 3000  },
  { id: "author_pro",          label: "Author Pro",                        words: 6000  },
  { id: "romanian_market",     label: "Romanian Market",                   words: 6000  },
  { id: "international",       label: "International",                     words: 6000  },
  { id: "premium_comparative", label: "Premium Comparative",               words: 20000 },
  { id: "full_manuscript",     label: "Full Manuscript",                   words: 80000 },
  { id: "ai_human",            label: "AI + Human Review",                 words: 80000 },
];
// Volume-uri disponibile per tier (în cuvinte)
const TIER_VOLUMES = {
  freemium:            [2000],
  basic:               [3000],
  author_pro:          [6000, 45000],
  romanian_market:     [6000, 45000, 80000, 120000],
  international:       [6000, 45000, 80000, 120000],
  premium_comparative: [6000, 45000, 80000, 120000],
  full_manuscript:     [80000, 120000],
  ai_human:            [80000, 120000],
};

function defaultVolume(tierId) {
  const vols = TIER_VOLUMES[tierId] || [6000];
  return vols[0];
}



// ─────────────────────────────────────────────────────────────────────────────
// GENURI
// ─────────────────────────────────────────────────────────────────────────────
const GENRES = [
  { id: "romance",                label: "Romance" },
  { id: "thriller",               label: "Thriller" },
  { id: "psychological_thriller", label: "Psychological Thriller" },
  { id: "crime_mystery",          label: "Crime / Mystery" },
  { id: "police_procedural",      label: "Police Procedural" },
  { id: "fantasy",                label: "Fantasy" },
  { id: "science_fiction",        label: "Science Fiction" },
  { id: "horror",                 label: "Horror" },
  { id: "historical_fiction",     label: "Historical Fiction" },
  { id: "literary_fiction",       label: "Literary Fiction" },
  { id: "young_adult",            label: "Young Adult" },
  { id: "romanian_fiction",       label: "Ficțiune cu tematică românească" },
];

// ─────────────────────────────────────────────────────────────────────────────
// UC + RML ENGINES
// ─────────────────────────────────────────────────────────────────────────────
const UC_ENGINES = [
  { id: "hook",        label: "Hook Engine",       weight: 5, desc: "Forța deschiderii" },
  { id: "pacing",      label: "Pacing Engine",      weight: 5, desc: "Controlul ritmului" },
  { id: "structure",   label: "Structure Engine",   weight: 8, desc: "Arhitectura narativă" },
  { id: "character",   label: "Character Engine",   weight: 8, desc: "Calitatea personajelor" },
  { id: "dialogue",    label: "Dialogue Engine",    weight: 4, desc: "Eficiența dialogului" },
  { id: "style",       label: "Style Engine",       weight: 5, desc: "Vocea și stilul" },
  { id: "emotional",   label: "Emotional Engine",   weight: 5, desc: "Impactul emoțional" },
  { id: "binge",       label: "Binge Engine",       weight: 5, desc: "Retenția cititorului" },
  { id: "readability", label: "Readability Engine", weight: 5, desc: "Claritatea lecturii" },
];

const RML_ENGINES = [
  { id: "reader_segmentation", label: "Reader Segmentation",   weight: 15, desc: "Segmentul de cititori români" },
  { id: "distribution_fit",    label: "Distribution Fit",      weight: 15, desc: "Canale de distribuție viabile" },
  { id: "cultural_resonance",  label: "Cultural Resonance",    weight: 15, desc: "Rezonanță cu teme românești" },
  { id: "digital_audio",       label: "Digital & Audio",       weight: 10, desc: "Potențial ebook, audiobook" },
  { id: "event_fair",          label: "Event & Fair",          weight: 10, desc: "Potențial lansări și târguri" },
  { id: "author_platform",     label: "Author Platform",       weight: 10, desc: "Rolul autorului în promovare" },
  { id: "small_press",         label: "Small Press Viability", weight: 10, desc: "Potrivire indie / self-publishing" },
  { id: "price_format",        label: "Price & Format Fit",    weight: 10, desc: "Format editorial optim" },
  { id: "media_fit",           label: "Media / Radio / Podcast",weight: 5, desc: "Potențial media culturală" },
];

// ─────────────────────────────────────────────────────────────────────────────
// MODULE DE GEN
// ─────────────────────────────────────────────────────────────────────────────
const GENRE_MODULES = {
  romance: {
    label: "Romance Module",
    question: "Cititorul investește emoțional în relația principală?",
    engines: [
      { id: "romantic_hook",    label: "Romantic Hook",    weight: 10, desc: "Promisiunea romantică din primele capitole" },
      { id: "chemistry",        label: "Chemistry Engine", weight: 25, desc: "Energia și tensiunea dintre personaje" },
      { id: "romantic_tension", label: "Romantic Tension", weight: 20, desc: "Slow burn, obstacole, apropiere–respingere" },
      { id: "emotional_bond",   label: "Emotional Bond",   weight: 15, desc: "Profunzimea legăturii emoționale" },
      { id: "intimacy",         label: "Intimacy Engine",  weight: 10, desc: "Relevanța scenelor romantice/intime" },
      { id: "trope_execution",  label: "Trope Execution",  weight: 10, desc: "Execuția trope-urilor de gen" },
      { id: "hea_hfn",          label: "HEA/HFN Engine",   weight: 10, desc: "Satisfacția finalului romantic" },
    ],
    promptSection: `Score as ROMANCE MODULE (0-10 each):
- romantic_hook: Romantic promise in opening chapters, anticipation, initial tension
- chemistry: Energy between characters — attraction, contrast, verbal/physical tension
- romantic_tension: Slow burn, push-pull, jealousy, real obstacles
- emotional_bond: Vulnerability, confession, mutual transformation
- intimacy: Narrative function, relational progress, memorability
- trope_execution: Trope clarity, organic integration, freshness
- hea_hfn: Emotional resolution, credibility, payoff, reader satisfaction
Flag: no spark, instalove, undifferentiated characters, absent HEA without justification.`,
  },
  thriller: {
    label: "Thriller Module",
    question: "Miza e clară, pericolul e real, tensiunea crește susținut?",
    engines: [
      { id: "threat_clarity",   label: "Threat Clarity",   weight: 20, desc: "Claritatea și credibilitatea amenințării" },
      { id: "pacing_tension",   label: "Pacing & Tension", weight: 25, desc: "Ritmul și escaladarea tensiunii" },
      { id: "stakes",           label: "Stakes Engine",    weight: 20, desc: "Miza: personală, socială, fizică" },
      { id: "plot_twist",       label: "Plot Twist Engine",weight: 20, desc: "Calitatea twisturilor și a reveal-urilor" },
      { id: "protagonist_peril",label: "Protagonist Peril",weight: 15, desc: "Vulnerabilitatea și pericolul protagonistului" },
    ],
    promptSection: `Score as THRILLER MODULE (0-10 each):
- threat_clarity: Is the threat clear, credible, escalating?
- pacing_tension: Rhythm — scene distribution, cliffhangers, dead zones
- stakes: Personal, physical, professional, social stakes — are they real?
- plot_twist: Twists — surprise, logic, preparation, payoff
- protagonist_peril: Is the protagonist genuinely at risk?
Flag: no real danger, unmotivated twists, flat protagonist, tension that deflates.`,
  },
  psychological_thriller: {
    label: "Psychological Thriller Module",
    question: "Manuscrisul produce anxietate, paranoia sau disconfort psihologic real?",
    engines: [
      { id: "psychological_pressure", label: "Psychological Pressure",  weight: 20, desc: "Anxietate, paranoia, rușine, obsesie" },
      { id: "unreliable_perception",  label: "Unreliable Perception",   weight: 20, desc: "Narator nesigur, contradicții, autoînșelare" },
      { id: "gaslighting_manipulation",label:"Gaslighting & Manipulation",weight:15, desc: "Rescrierea realității, control, izolare" },
      { id: "obsession_pattern",      label: "Obsession Pattern",       weight: 15, desc: "Obsesia: intensitate și dramatizare" },
      { id: "reality_distortion",     label: "Reality Distortion",      weight: 15, desc: "Raport eveniment real / percepție personaj" },
      { id: "moral_ambiguity",        label: "Moral Ambiguity",         weight: 15, desc: "Zona gri morală, vină, complicitate" },
    ],
    promptSection: `Score as PSYCHOLOGICAL THRILLER MODULE (0-10 each):
- psychological_pressure: Anxiety, paranoia, guilt, shame, obsession, inner deterioration
- unreliable_perception: Unsafe memory, contradictions, self-deception, unreliable narrator
- gaslighting_manipulation: Reality rewriting, emotional invalidation, control, isolation
- obsession_pattern: Obsession intensity and dramatization
- reality_distortion: Real event vs character perception ratio
- moral_ambiguity: Guilt, complicity, justification, moral consequence
Flag: decorative trauma, implausible manipulation, involuntary confusion.`,
  },
  crime_mystery: {
    label: "Crime / Mystery Module",
    question: "Misterul este construit corect, logic și satisfăcător?",
    engines: [
      { id: "central_question", label: "Central Question", weight: 15, desc: "Claritatea întrebării principale" },
      { id: "clue_analysis",    label: "Clue Analysis",    weight: 25, desc: "Calitatea indiciilor: fair-play, payoff" },
      { id: "red_herrings",     label: "Red Herrings",     weight: 15, desc: "Pistele false: credibilitate, demontare logică" },
      { id: "suspect_web",      label: "Suspect Web",      weight: 20, desc: "Rețeaua de suspecți: motivații, diferențiere" },
      { id: "final_solution",   label: "Final Solution",   weight: 25, desc: "Rezolvarea: surpriză + logică + inevitabilitate" },
    ],
    promptSection: `Score as CRIME / MYSTERY MODULE (0-10 each):
- central_question: Clarity of main question
- clue_analysis: Clues — relevance, placement, fair-play, deducibility
- red_herrings: False leads — credibility, logical dismantling
- suspect_web: Suspects — motives, means, opportunities, differentiation
- final_solution: Resolution — surprise, logic, retrospective inevitability
Flag: incorrectly hidden clues, late introduction of guilty party, arbitrary solution.`,
  },
  police_procedural: {
    label: "Police Procedural Module",
    question: "Ancheta avansează credibil prin proceduri, logică și presiune instituțională?",
    engines: [
      { id: "procedural_realism",      label: "Procedural Realism",      weight: 30, desc: "Realism procedural: mandate, interogatorii" },
      { id: "investigation_logic",     label: "Investigation Logic",      weight: 35, desc: "Logica anchetei: ipoteze, verificări" },
      { id: "institutional_pressure",  label: "Institutional Pressure",   weight: 20, desc: "Presiunea sistemului: superiori, presă" },
      { id: "investigator_competence", label: "Investigator Competence",  weight: 15, desc: "Competența și vulnerabilitatea investigatorului" },
    ],
    promptSection: `Score as POLICE PROCEDURAL MODULE (0-10 each):
- procedural_realism: Decision chain, interrogations, warrants, bureaucracy
- investigation_logic: Hypotheses, verifications, suspect elimination
- institutional_pressure: Superiors, politics, press, limited resources
- investigator_competence: Method, intuition, errors, obsession, fatigue
Flag: officers ignoring procedures without consequences, conveniently discovered evidence.`,
  },
  fantasy: {
    label: "Fantasy Module",
    question: "Lumea fantastică este coerentă, memorabilă și capabilă să susțină povestea?",
    engines: [
      { id: "worldbuilding",        label: "Worldbuilding Engine",    weight: 25, desc: "Construcția lumii: geografie, cultură, economie" },
      { id: "magic_system",         label: "Magic System",            weight: 20, desc: "Coerența magiei: reguli, costuri, limite" },
      { id: "lore",                 label: "Lore Engine",             weight: 15, desc: "Mitologie: legende, profeții, cosmologie" },
      { id: "political_social",     label: "Political / Social",      weight: 15, desc: "Facțiuni, conducere, tensiuni sociale" },
      { id: "internal_consistency", label: "Internal Consistency",    weight: 15, desc: "Continuitate, regulile lumii respectate" },
      { id: "sense_of_wonder",      label: "Sense of Wonder",         weight: 10, desc: "Fascinație: imagini memorabile, mister" },
    ],
    promptSection: `Score as FANTASY MODULE (0-10 each):
- worldbuilding: Geography, culture, history, economy, religion, social classes
- magic_system: Rules, costs, limits, consequences, originality
- lore: Legends, past, conflicts, prophecies, cosmology
- political_social: Factions, leadership, conflicts, alliances
- internal_consistency: Continuity, avoiding contradictions
- sense_of_wonder: Memorable imagery, mystery, strangeness
Flag: infodump, magic without limits, generic medieval world, rules changed for convenience.`,
  },
  science_fiction: {
    label: "Science Fiction Module",
    question: "Ideea speculativă este suficient de puternică și coerentă pentru a susține povestea?",
    engines: [
      { id: "big_idea",      label: "Big Idea Engine",      weight: 25, desc: "Forța conceptului SF: originalitate, potențial filozofic" },
      { id: "extrapolation", label: "Extrapolation Engine", weight: 20, desc: "Logica speculativă: consecințe sociale, etice" },
      { id: "plausibility",  label: "Plausibility Engine",  weight: 20, desc: "Plauzibilitate internă: consistență, limite" },
      { id: "innovation",    label: "Innovation Engine",    weight: 15, desc: "Prospețimea speculației, evitarea clișeelor" },
      { id: "human_impact",  label: "Human Impact Engine",  weight: 20, desc: "Efectul ideii asupra personajelor" },
    ],
    promptSection: `Score as SCIENCE FICTION MODULE (0-10 each):
- big_idea: Originality, clarity, philosophical and dramatic potential
- extrapolation: Social cause-effect, technological impact, ethical consequences
- plausibility: Consistency, limits, no contradictions
- innovation: Novelty, reinterpretation, avoiding worn tropes
- human_impact: Identity change, moral conflict, effects on relationships
Flag: interesting concept with weak characters, decorative technology.`,
  },
  horror: {
    label: "Horror Module",
    question: "Manuscrisul produce frică, neliniște sau disconfort memorabil?",
    engines: [
      { id: "fear",                label: "Fear Engine",           weight: 20, desc: "Frica directă: amenințare, vulnerabilitate, șoc" },
      { id: "dread",               label: "Dread Engine",          weight: 25, desc: "Neliniștea lentă: presimțire, inevitabilitate" },
      { id: "monster_threat",      label: "Monster / Threat Design",weight:20, desc: "Forța entității amenințătoare" },
      { id: "atmosphere",          label: "Atmosphere Engine",     weight: 20, desc: "Densitatea atmosferei: decor, simțuri, izolare" },
      { id: "psychological_horror",label: "Psychological Horror",  weight: 15, desc: "Deteriorarea mentală: paranoia, disociere" },
    ],
    promptSection: `Score as HORROR MODULE (0-10 each):
- fear: Threat, vulnerability, danger, anticipation, shock
- dread: Foreboding, oppressive atmosphere, inevitability, slow anxiety
- monster_threat: Creature, ghost, house, cult — strength and originality
- atmosphere: Setting, senses, darkness, isolation, strangeness
- psychological_horror: Paranoia, obsession, guilt, dissociation
Flag: shock-only horror, generic monster, declared-not-felt atmosphere.`,
  },
  historical_fiction: {
    label: "Historical Fiction Module",
    question: "Epoca istorică este integrată organic în poveste sau funcționează doar ca decor?",
    engines: [
      { id: "historical_accuracy",    label: "Historical Accuracy",    weight: 30, desc: "Corectitudinea elementelor istorice" },
      { id: "historical_integration", label: "Historical Integration",  weight: 30, desc: "Epoca influențează deciziile și miza" },
      { id: "cultural_authenticity",  label: "Cultural Authenticity",   weight: 25, desc: "Credibilitatea mentalităților epocii" },
      { id: "research_depth",         label: "Research Depth",          weight: 15, desc: "Profunzimea cercetării, evitarea anacronismelor" },
    ],
    promptSection: `Score as HISTORICAL FICTION MODULE (0-10 each):
- historical_accuracy: Dates, events, institutions, clothing, technology, customs
- historical_integration: Era influences decisions, social norms create real stakes
- cultural_authenticity: Values, prejudices, religion, family, social language
- research_depth: Specific details, avoiding anachronisms, authentic daily life
Flag: modern characters in period costumes, decorative era, anachronisms.`,
  },
  literary_fiction: {
    label: "Literary Fiction Module",
    question: "Manuscrisul produce profunzime estetică, psihologică sau tematică reală?",
    engines: [
      { id: "theme_depth",              label: "Theme Depth Engine",      weight: 25, desc: "Profunzimea temelor: identitate, moarte, vină" },
      { id: "symbolism",                label: "Symbolism Engine",         weight: 15, desc: "Funcția simbolurilor: recurență, subtilitate" },
      { id: "psychological_complexity", label: "Psychological Complexity", weight: 25, desc: "Contradicții, ambivalență, autoînșelare" },
      { id: "voice",                    label: "Voice Engine",             weight: 25, desc: "Originalitatea vocii: ritm, perspectivă" },
      { id: "ambiguity",                label: "Ambiguity Engine",         weight: 10, desc: "Ambiguitate productivă, interpretări multiple" },
    ],
    promptSection: `Score as LITERARY FICTION MODULE (0-10 each):
- theme_depth: Identity, death, love, guilt, memory, faith — depth and dramatization
- symbolism: Recurrence, subtlety, integration, ambiguity (never explained symbols)
- psychological_complexity: Contradictions, self-deception, ambivalence, transformation
- voice: Own rhythm, distinct perspective, sentence as stylistic identity
- ambiguity: Multiple interpretations, fertile irresolution
Flag: obscurity confused with depth, explained symbols, stated-not-dramatized themes.`,
  },
  young_adult: {
    label: "Young Adult Module",
    question: "Manuscrisul vorbește credibil și relevant pentru publicul tânăr?",
    engines: [
      { id: "voice_authenticity", label: "Voice Authenticity",   weight: 25, desc: "Credibilitatea vocii tinere: naturalețe" },
      { id: "identification",     label: "Identification Engine", weight: 20, desc: "Capacitatea cititorului de a se regăsi" },
      { id: "coming_of_age",      label: "Coming-of-Age Engine",  weight: 20, desc: "Evoluția spre maturizare: decizie, asumare" },
      { id: "relevance",          label: "Relevance Engine",      weight: 20, desc: "Actualitatea temelor: identitate, anxietate" },
      { id: "accessibility",      label: "Accessibility Engine",  weight: 15, desc: "Adecvarea stilului și ritmului" },
    ],
    promptSection: `Score as YOUNG ADULT MODULE (0-10 each):
- voice_authenticity: Naturalness, intensity, language, perspective, vulnerability
- identification: Recognizable emotions, age-appropriate conflicts, social pressure
- coming_of_age: Real decisions, loss, self-discovery, conflict with authority
- relevance: Social anxiety, identity, technology, belonging, marginalization
- accessibility: Clarity, energy, efficient chapters, direct emotion
Flag: adult voice disguised as teen, forced slang, moralizing, no transformation.`,
  },
  romanian_fiction: {
    label: "Ficțiune cu Tematică Românească",
    question: "Manuscrisul transformă specificul românesc într-o experiență literară autentică?",
    engines: [
      { id: "cultural_authenticity", label: "Romanian Cultural Authenticity", weight: 20, desc: "Autenticitatea spațiului social și mentalităților" },
      { id: "local_setting",         label: "Local Setting Engine",           weight: 15, desc: "Forța spațiului local: sat, oraș, diaspora" },
      { id: "social_memory",         label: "Social Memory Engine",           weight: 15, desc: "Memoria socială: comunism, tranziție, migrație" },
      { id: "mentality_conflict",    label: "Mentality & Social Conflict",    weight: 20, desc: "Rușine, aparență, presiunea comunității" },
      { id: "language_register",     label: "Language & Register",            weight: 15, desc: "Autenticitatea limbajului: registre, oralitate" },
      { id: "tradition_modernity",   label: "Tradition vs Modernity",         weight: 10, desc: "Conflictul vechi–nou: generații, sat–oraș" },
      { id: "market_resonance",      label: "Romanian Market Resonance",      weight:  5, desc: "Potențialul de rezonanță cu cititorii români" },
    ],
    promptSection: `Score as ROMANIAN-THEMED FICTION MODULE (0-10 each):
- cultural_authenticity: Credibility of social space, family relations, local mentalities
- local_setting: Village, small town, Bucharest, diaspora — influences conflict?
- social_memory: Communism, post-communism, transition, migration — dramatized, not labeled
- mentality_conflict: Shame, appearance vs truth, community pressure
- language_register: Register differentiation, credible orality, regionalisms
- tradition_modernity: Old–new conflict, generational tensions
- market_resonance: Potential to resonate with Romanian readers
Flag: Romanian setting as mere background, labeled-not-dramatized history.`,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// VERDICTE
// ─────────────────────────────────────────────────────────────────────────────
const VERDICTS = {
  publish_now:    { label: "Publish Now",    color: "#059669", bg: "#d1fae5" },
  minor_revision: { label: "Minor Revision", color: "#0284c7", bg: "#e0f2fe" },
  major_revision: { label: "Major Revision", color: "#d97706", bg: "#fef3c7" },
  structural_edit:{ label: "Structural Edit",color: "#ea580c", bg: "#ffedd5" },
  rewrite:        { label: "Rewrite",        color: "#dc2626", bg: "#fee2e2" },
  reject:         { label: "Reject",         color: "#7f1d1d", bg: "#fecaca" },
};

function getVerdict(score) {
  if (score >= 85) return "publish_now";
  if (score >= 72) return "minor_revision";
  if (score >= 58) return "major_revision";
  if (score >= 44) return "structural_edit";
  if (score >= 30) return "rewrite";
  return "reject";
}

const RML_VERDICTS = [
  { min: 80, label: "Foarte potrivit pentru piața românească",                     color: "#059669", bg: "#d1fae5" },
  { min: 65, label: "Potrivit pentru piața românească, cu poziționare atentă",     color: "#0284c7", bg: "#e0f2fe" },
  { min: 50, label: "Potrivit mai ales pentru indie / self-publishing asistat",    color: "#d97706", bg: "#fef3c7" },
  { min: 35, label: "Potențial românesc moderat, nișă locală",                    color: "#ea580c", bg: "#ffedd5" },
  { min: 0,  label: "Potențial românesc redus în forma actuală",                  color: "#dc2626", bg: "#fee2e2" },
];
const getRmlVerdict = (score) => RML_VERDICTS.find(v => score >= v.min) || RML_VERDICTS[RML_VERDICTS.length - 1];

// ─────────────────────────────────────────────────────────────────────────────
// PROMPT BUILDERS
// ─────────────────────────────────────────────────────────────────────────────

// Prompt complet FEUR (tiers paid)
function buildPrompt(title, author, genreId, excerpt, reportLang = "Romanian") {
  const gmod = GENRE_MODULES[genreId];
  const genreLabel = GENRES.find(g => g.id === genreId)?.label || genreId;
  return `You are FEUR (Fișa Editorială Universală România), the expert editorial evaluation system for fiction manuscripts.

IMPORTANT: Write ALL text values in the JSON response in ${reportLang}.

MANUSCRIPT:
Title: ${title}
Author: ${author || "Unknown"}
Declared genre: ${genreLabel}

EXCERPT:
${excerpt}

═══ LAYER 1: UNIVERSAL CORE (50% of final score) ═══
Score each engine 0-10:
- hook: Opening strength — tension, voice, curiosity, narrative promise
- pacing: Narrative rhythm — scene distribution, dead zones, chapter endings
- structure: Architecture — coherence, cause-effect, inciting incident, climax, plot holes
- character: Characters — credibility, motivation, evolution, memorability
- dialogue: Dialogue — naturalness, efficiency, voice differentiation
- style: Style — distinct narrative voice, sentence craft, tone
- emotional: Emotional impact — resonance, empathy, intensity
- binge: Reader retention — desire to continue, active questions, traction
- readability: Clarity — legibility, fluency, audience appropriateness

═══ LAYER 2: ${gmod.label.toUpperCase()} (40% of final score) ═══
Central question: ${gmod.question}
${gmod.promptSection}

═══ LAYER 3: ROMANIAN MARKET LAYER (separate score 0-100) ═══
Score each 0-10:
- reader_segmentation: Romanian reader segment (urban commercial, BookTok RO, diaspora, literary, audio)
- distribution_fit: Viable channels (physical, online, indie, POD, ebook, audiobook)
- cultural_resonance: Resonance with Romanian themes/mentalities
- digital_audio: Ebook, audiobook, audio serialization potential
- event_fair: Launch events, fairs (Gaudeamus), book clubs, libraries
- author_platform: Social media, public appearances, podcast fit
- small_press: Indie/self-publishing fit — clear niche, controllable costs
- price_format: Optimal format and price sensitivity
- media_fit: Cultural media potential (radio, podcasts, cultural columns)

Also evaluate:
- big_publisher_fit (0-100): Fit for major publisher
- indie_publisher_fit (0-100): Fit for indie/self-publishing
- primary_segment: Most fitting Romanian reader segment (string, in ${reportLang})
- distribution_channels: Top 3 recommended channels (in ${reportLang})
- format_recommendation: Recommended editorial format (in ${reportLang})
- marketing_logline: Marketing logline, max 2 sentences (in ${reportLang})
- marketing_keywords: 5 marketing keywords (in ${reportLang})

═══ PRELIMINARY CHECKS ═══
- grammar_issues: Grammatical errors, missing diacritics (in ${reportLang})
- genre_fit: Compatibility with declared genre ${genreLabel}, 2 sentences (in ${reportLang})

═══ STRICT JSON RESPONSE (no text outside JSON) ═══
{
  "uc": { "hook":N,"pacing":N,"structure":N,"character":N,"dialogue":N,"style":N,"emotional":N,"binge":N,"readability":N },
  "genre": { ${gmod.engines.map(e => `"${e.id}":N`).join(",")} },
  "genre_verdict": "string in ${reportLang}",
  "rml": { "reader_segmentation":N,"distribution_fit":N,"cultural_resonance":N,"digital_audio":N,"event_fair":N,"author_platform":N,"small_press":N,"price_format":N,"media_fit":N },
  "big_publisher_fit": N,
  "indie_publisher_fit": N,
  "primary_segment": "string in ${reportLang}",
  "distribution_channels": ["ch1","ch2","ch3"],
  "format_recommendation": "string in ${reportLang}",
  "marketing_logline": "string in ${reportLang}",
  "marketing_keywords": ["kw1","kw2","kw3","kw4","kw5"],
  "grammar_issues": "string in ${reportLang}",
  "genre_fit": "string in ${reportLang}",
  "strengths": ["s1","s2","s3"],
  "weaknesses": ["w1","w2","w3"],
  "recommendations": ["r1","r2","r3"],
  "summary": "3-4 sentences in ${reportLang}",
  "confidence": N
}`;
}

// Prompt scurt freemium — returnează doar hook preview
function buildFreemiumPrompt(title, author, genreId, excerpt) {
  const genreLabel = GENRES.find(g => g.id === genreId)?.label || genreId;
  return `You are FEUR, an editorial AI. Analyze this manuscript excerpt briefly.

MANUSCRIPT:
Title: ${title}
Author: ${author || "Unknown"}
Genre: ${genreLabel}

EXCERPT (first 1500 words):
${excerpt}

Return ONLY valid JSON (no text outside):
{
  "hook": N,
  "genre_confirmed": "string",
  "strength": "one sentence in Romanian — strongest point",
  "issue": "one sentence in Romanian — main weakness",
  "recommended_tier": "author_pro",
  "summary": "2 sentences in Romanian about the overall impression"
}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCUL SCORURI
// ─────────────────────────────────────────────────────────────────────────────
function computeScores(ucRaw, genreRaw, rmlRaw, genreId) {
  const clamp = v => typeof v === "number" ? Math.min(10, Math.max(0, v)) : 5;
  const uc = {};
  UC_ENGINES.forEach(e => { uc[e.id] = clamp(ucRaw?.[e.id]); });
  const gmod = GENRE_MODULES[genreId];
  const genre = {};
  gmod.engines.forEach(e => { genre[e.id] = clamp(genreRaw?.[e.id]); });
  const rml = {};
  RML_ENGINES.forEach(e => { rml[e.id] = clamp(rmlRaw?.[e.id]); });

  let ucScore = 0;
  UC_ENGINES.forEach(e => { ucScore += uc[e.id] * (e.weight / 50) * 10; });

  let gSum = 0, gW = 0;
  gmod.engines.forEach(e => { gSum += genre[e.id] * e.weight; gW += e.weight; });
  const genreScore10 = gSum / gW;

  let rSum = 0, rW = 0;
  RML_ENGINES.forEach(e => { rSum += rml[e.id] * e.weight; rW += e.weight; });
  const rmlScore100 = Math.round((rSum / rW) * 10);

  const feur = Math.round(Math.min(100, Math.max(0,
    ucScore * 0.5 + genreScore10 * 0.4 + (rmlScore100 / 100) * 10 * 0.1
  )));

  return {
    uc, genre, rml,
    ucScore: Math.round(ucScore * 10) / 10,
    genreScore10: Math.round(genreScore10 * 10) / 10,
    rmlScore100,
    feur,
  };
}

// PDF export via HTML print — no external library needed

// ─────────────────────────────────────────────────────────────────────────────
// UI COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function ScoreGauge({ score, max = 100, size = 100 }) {
  const r = 40, circ = 2 * Math.PI * r, filled = (score / max) * circ;
  const pct = score / max;
  const color = pct >= 0.72 ? "#059669" : pct >= 0.44 ? "#d97706" : "#dc2626";
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
      <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
        strokeDasharray={`${filled} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 50 50)" style={{ transition: "stroke-dasharray 1.2s ease" }} />
      <text x="50" y="46" textAnchor="middle" fontSize="18" fontWeight="700" fill={color}>{Math.round(score)}</text>
      <text x="50" y="60" textAnchor="middle" fontSize="9" fill="#9ca3af">/ {max}</text>
    </svg>
  );
}

function MiniGauge({ score }) {
  const r = 15, circ = 2 * Math.PI * r, filled = (score / 10) * circ;
  const color = score >= 7 ? "#059669" : score >= 5 ? "#d97706" : "#dc2626";
  return (
    <svg width="38" height="38" viewBox="0 0 38 38">
      <circle cx="19" cy="19" r={r} fill="none" stroke="#e5e7eb" strokeWidth="4" />
      <circle cx="19" cy="19" r={r} fill="none" stroke={color} strokeWidth="4"
        strokeDasharray={`${filled} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 19 19)" style={{ transition: "stroke-dasharray 0.9s ease" }} />
      <text x="19" y="23" textAnchor="middle" fontSize="9.5" fontWeight="700" fill={color}>{score.toFixed(1)}</text>
    </svg>
  );
}

function EngineRow({ engine, score }) {
  const color = score >= 7 ? "#059669" : score >= 5 ? "#d97706" : "#dc2626";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
      <MiniGauge score={score} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 500, fontFamily: "sans-serif" }}>{engine.label}</span>
          <span style={{ fontSize: 11, color: "#9ca3af", fontFamily: "sans-serif", marginLeft: 6 }}>×{engine.weight}%</span>
        </div>
        <div style={{ height: 4, background: "#f3f4f6", borderRadius: 4 }}>
          <div style={{ height: "100%", width: `${score * 10}%`, background: color, borderRadius: 4, transition: "width 1s ease" }} />
        </div>
        <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 0", fontFamily: "sans-serif" }}>{engine.desc}</p>
      </div>
    </div>
  );
}

function ScoreCard({ label, score, max = 100, sub }) {
  const pct = score / max;
  const color = pct >= 0.72 ? "#059669" : pct >= 0.44 ? "#d97706" : "#dc2626";
  return (
    <div style={{ background: "#f9fafb", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
      <p style={{ fontSize: 11, color: "#9ca3af", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</p>
      <p style={{ fontSize: 22, fontWeight: 700, color, margin: "0 0 2px" }}>
        {Math.round(score)}<span style={{ fontSize: 12, fontWeight: 400, color: "#9ca3af" }}>/{max}</span>
      </p>
      {sub && <p style={{ fontSize: 11, color: "#6b7280", margin: 0 }}>{sub}</p>}
    </div>
  );
}

function Tag({ text, color = "#6b7280", bg = "#f3f4f6" }) {
  return (
    <span style={{ display: "inline-block", background: bg, color, fontSize: 12, padding: "3px 10px", borderRadius: 12, marginRight: 5, marginBottom: 5 }}>
      {text}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT PDF (full report)
// ─────────────────────────────────────────────────────────────────────────────
async function doExportPDF(report) {
  const r = report;
  const v = VERDICTS[r.verdict] || {};
  const rmlV = getRmlVerdict(r.rmlScore100);
  const filename = `FEUR-${r.title.replace(/\s+/g,"-").slice(0,30)}-${new Date().toISOString().slice(0,10)}`;

  const verdictColor = v.color || "#374151";
  const rmlColor = rmlV.color || "#059669";

  const esc = (s) => String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

  const listItems = (arr, color) =>
    (arr||[]).map((s,i) => `<li style="color:${color};margin-bottom:6px">${i+1}. ${esc(s)}</li>`).join("");

  const ucRows = r.uc ? UC_ENGINES.map(e => {
    const sc = r.uc[e.id] || 0;
    const c = sc >= 7 ? "#059669" : sc >= 5 ? "#d97706" : "#dc2626";
    return `<tr><td style="padding:4px 8px;font-size:12px">${esc(e.label)}</td>
      <td style="padding:4px 8px;text-align:center;font-weight:700;color:${c};font-size:12px">${sc.toFixed(1)}</td>
      <td style="padding:4px 8px;font-size:11px;color:#6b7280">×${e.weight}%</td></tr>`;
  }).join("") : "";

  const gmod = r.gmod;
  const genreRows = gmod && r.genre ? gmod.engines.map(e => {
    const sc = r.genre[e.id] || 0;
    const c = sc >= 7 ? "#059669" : sc >= 5 ? "#d97706" : "#dc2626";
    return `<tr><td style="padding:4px 8px;font-size:12px">${esc(e.label)}</td>
      <td style="padding:4px 8px;text-align:center;font-weight:700;color:${c};font-size:12px">${sc.toFixed(1)}</td>
      <td style="padding:4px 8px;font-size:11px;color:#6b7280">×${e.weight}%</td></tr>`;
  }).join("") : "";

  const rmlRows = r.rml ? RML_ENGINES.map(e => {
    const sc = r.rml[e.id] || 0;
    const c = sc >= 7 ? "#059669" : sc >= 5 ? "#d97706" : "#dc2626";
    return `<tr><td style="padding:4px 8px;font-size:12px">${esc(e.label)}</td>
      <td style="padding:4px 8px;text-align:center;font-weight:700;color:${c};font-size:12px">${sc.toFixed(1)}</td>
      <td style="padding:4px 8px;font-size:11px;color:#6b7280">×${e.weight}%</td></tr>`;
  }).join("") : "";

  const html = `<!DOCTYPE html>
<html lang="ro">
<head>
<meta charset="UTF-8">
<title>${esc(filename)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;1,400&family=Inter:wght@400;500;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', Georgia, serif; color: #1f2937; background: #fff; font-size: 13px; line-height: 1.6; }
  .page { max-width: 740px; margin: 0 auto; padding: 40px 48px; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none; }
    .page { padding: 20px 32px; }
    .page-break { page-break-before: always; }
  }
  .header { background: #1d4ed8; color: #fff; padding: 18px 24px; border-radius: 10px; margin-bottom: 24px; }
  .header h1 { font-size: 17px; font-weight: 700; margin-bottom: 3px; }
  .header p { font-size: 11px; opacity: 0.7; }
  .meta { margin-bottom: 6px; font-size: 13px; color: #6b7280; }
  .title { font-family: 'Crimson Pro', Georgia, serif; font-size: 26px; font-weight: 600; margin-bottom: 4px; }
  .score-box { border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px 20px; margin: 16px 0; display: flex; gap: 32px; align-items: flex-start; background: #f9fafb; }
  .score-main { text-align: center; min-width: 80px; }
  .score-num { font-size: 42px; font-weight: 700; line-height: 1; }
  .score-label { font-size: 10px; color: #9ca3af; margin-top: 2px; }
  .verdict-badge { display: inline-block; padding: 3px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; margin-top: 6px; }
  .score-details { flex: 1; }
  .score-row { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 8px; }
  .score-item { font-size: 12px; color: #6b7280; }
  .score-item strong { color: #374151; }
  .rml-label { font-size: 12px; font-weight: 600; margin-top: 6px; }
  .summary { font-family: 'Crimson Pro', Georgia, serif; font-size: 15px; line-height: 1.75; color: #374151; margin: 20px 0; font-style: italic; padding: 16px 20px; background: #f8fafc; border-left: 3px solid #1d4ed8; border-radius: 0 8px 8px 0; }
  .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #1e40af; background: #eff6ff; padding: 6px 12px; border-radius: 6px; margin: 20px 0 10px; }
  .sw-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
  .sw-box { padding: 12px 14px; border-radius: 8px; }
  .sw-box.strengths { background: #f0fdf4; border: 1px solid #bbf7d0; }
  .sw-box.weaknesses { background: #fff7ed; border: 1px solid #fed7aa; }
  .sw-box h4 { font-size: 12px; font-weight: 600; margin-bottom: 6px; }
  .sw-box.strengths h4 { color: #065f46; }
  .sw-box.weaknesses h4 { color: #9a3412; }
  .sw-box ul { list-style: none; padding: 0; }
  .rec-list { list-style: none; padding: 0; }
  .rec-list li { padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 13px; color: #1e3a8a; }
  .rec-list li:last-child { border-bottom: none; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th { background: #f3f4f6; font-size: 11px; font-weight: 600; padding: 6px 8px; text-align: left; color: #6b7280; }
  tr:nth-child(even) td { background: #f9fafb; }
  .market-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 12px 0; }
  .market-card { text-align: center; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; }
  .market-card .val { font-size: 20px; font-weight: 700; }
  .market-card .lbl { font-size: 10px; color: #9ca3af; margin-top: 2px; }
  .logline-box { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; padding: 12px 14px; margin: 10px 0; }
  .logline-box p { font-style: italic; font-family: 'Crimson Pro', Georgia, serif; font-size: 14px; color: #0c4a6e; }
  .kw { display: inline-block; background: #dbeafe; color: #1e40af; font-size: 11px; padding: 2px 8px; border-radius: 10px; margin: 2px; }
  .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 10px; color: #9ca3af; }
  .print-btn { position: fixed; bottom: 24px; right: 24px; padding: 12px 24px; background: #1d4ed8; color: #fff; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 12px rgba(29,78,216,0.3); }
</style>
</head>
<body>
<div class="page">

  <div class="header">
    <h1>Raport FEUR · Narratiq EditorialAI România</h1>
    <p>FEUR-1 Architecture Specification v1.0 · ${esc(r.timestamp)}</p>
  </div>

  <div class="title">${esc(r.title)}</div>
  <div class="meta">${[r.author && `Autor: ${esc(r.author)}`, `Gen: ${esc(r.genreLabel)}`, `Încredere AI: ${r.confidence}%`].filter(Boolean).join(" · ")}</div>

  <div class="score-box">
    <div class="score-main">
      <div class="score-num" style="color:${verdictColor}">${r.feur}</div>
      <div class="score-label">/ 100 · FEUR</div>
      <div class="verdict-badge" style="background:${v.bg||'#f3f4f6'};color:${verdictColor}">${v.label||""}</div>
    </div>
    <div class="score-details">
      <div class="score-row">
        <div class="score-item">UC <strong>${r.ucScore}/50</strong></div>
        <div class="score-item">Gen <strong>${r.genreScore10}/10</strong></div>
        <div class="score-item">Încredere <strong>${r.confidence}%</strong></div>
      </div>
      <div class="score-row">
        <div class="score-item">Romanian Market Fit <strong>${r.rmlScore100}/100</strong></div>
        <div class="score-item">Editură mare <strong>${r.bigPub||0}/100</strong></div>
        <div class="score-item">Indie <strong>${r.indiePub||0}/100</strong></div>
      </div>
      <div class="rml-label" style="color:${rmlColor}">${esc(rmlV.label)}</div>
    </div>
  </div>

  ${r.summary ? `<div class="summary">${esc(r.summary)}</div>` : ""}

  <div class="sw-grid">
    <div class="sw-box strengths">
      <h4>✓ Puncte forte</h4>
      <ul>${listItems(r.strengths, "#065f46")}</ul>
    </div>
    <div class="sw-box weaknesses">
      <h4>⚠ Probleme detectate</h4>
      <ul>${listItems(r.weaknesses, "#9a3412")}</ul>
    </div>
  </div>

  <div class="section-title">Recomandări editoriale FEUR</div>
  <ul class="rec-list">${listItems(r.recommendations, "#1e3a8a")}</ul>

  ${ucRows ? `
  <div class="page-break"></div>
  <div class="section-title">Universal Core — 9 motoare</div>
  <table><thead><tr><th>Motor</th><th>Scor</th><th>Pondere</th></tr></thead><tbody>${ucRows}</tbody></table>
  ` : ""}

  ${genreRows && gmod ? `
  <div class="section-title">${esc(gmod.label)}</div>
  <table><thead><tr><th>Motor</th><th>Scor</th><th>Pondere</th></tr></thead><tbody>${genreRows}</tbody></table>
  ` : ""}

  ${rmlRows ? `
  <div class="section-title">Romanian Market Layer — 9 motoare</div>
  <div class="market-grid">
    <div class="market-card"><div class="val" style="color:${rmlColor}">${r.rmlScore100}</div><div class="lbl">Market Fit</div></div>
    <div class="market-card"><div class="val">${r.bigPub||0}</div><div class="lbl">Editură mare</div></div>
    <div class="market-card"><div class="val">${r.indiePub||0}</div><div class="lbl">Indie</div></div>
  </div>
  <table><thead><tr><th>Motor</th><th>Scor</th><th>Pondere</th></tr></thead><tbody>${rmlRows}</tbody></table>
  ${r.primarySegment ? `<p style="font-size:12px;color:#374151;margin:8px 0"><strong>Cititor principal:</strong> ${esc(r.primarySegment)}</p>` : ""}
  ${r.logline ? `<div class="logline-box"><p>${esc(r.logline)}</p></div>` : ""}
  ${r.keywords?.length ? `<div style="margin-top:6px">${r.keywords.map(k=>`<span class="kw">${esc(k)}</span>`).join("")}</div>` : ""}
  ` : ""}

  <div class="footer">
    Raport generat de Narratiq EditorialAI România · FEUR-1 Architecture Specification v1.0<br>
    Raport AI. Nu înlocuiește editorul uman.
  </div>

</div>

<button class="print-btn no-print" onclick="window.print()">⬇ Salvează PDF</button>

<script>
  document.title = "${esc(filename)}";
  setTimeout(() => window.print(), 600);
</script>
</body>
</html>`;

  const w = window.open("", "_blank");
  if (!w) { alert("Permite ferestrele pop-up pentru a descărca PDF-ul."); return; }
  w.document.write(html);
  w.document.close();
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT DOCX
// ─────────────────────────────────────────────────────────────────────────────
async function doExportDOCX(report) {
  const { Document, Packer, Paragraph, TextRun, AlignmentType } = await import("docx");
  const r = report;
  const v = VERDICTS[r.verdict];
  const rmlV = getRmlVerdict(r.rmlScore100);
  const DARK = "1F2937", GRAY = "6B7280";

  const para = (text, opts = {}) => new Paragraph({
    children: [new TextRun({ text: String(text||""), bold: opts.bold, italics: opts.italics, color: opts.color||DARK, size: opts.size||18 })],
    alignment: opts.align || AlignmentType.LEFT,
    spacing: { before: opts.before||60, after: opts.after||60 },
    heading: opts.heading,
  });

  const heading = (text) => para(text, { bold: true, color: "1E3A8A", size: 20, before: 180, after: 80 });
  const divider = () => new Paragraph({ children: [], border: { bottom: { color: "E5E7EB", size: 1, space: 1, style: "single" } }, spacing: { before: 80, after: 80 } });

  const children = [
    para("Raport FEUR · Narratiq EditorialAI România", { bold: true, size: 26, color: "1D4ED8" }),
    para(`${r.title}${r.author ? " · " + r.author : ""} · ${r.genreLabel} · ${r.timestamp}`, { color: GRAY, size: 16 }),
    divider(),
    para(`FEUR Base Score: ${r.feur}/100 · ${v.label}`, { bold: true, color: v.color.replace("#",""), size: 22 }),
    para(`UC: ${r.ucScore}/50 · Gen: ${r.genreScore10}/10 · Încredere: ${r.confidence}%`, { color: GRAY }),
    para(`Romanian Market Fit: ${r.rmlScore100}/100 · ${rmlV.label}`, { bold: true }),
    divider(),
    ...(r.summary ? [heading("Rezumat editorial"), para(r.summary, { italics: true, color: "374151" })] : []),
    heading("✓ Puncte forte"),
    ...(r.strengths||[]).map((s,i) => para(`${i+1}. ${s}`, { color: "065F46" })),
    heading("⚠ Probleme detectate"),
    ...(r.weaknesses||[]).map((w,i) => para(`${i+1}. ${w}`, { color: "9A3412" })),
    heading("Recomandări editoriale"),
    ...(r.recommendations||[]).map((rec,i) => para(`${i+1}. ${rec}`, { color: "1E3A8A" })),
    divider(),
    para("Raport generat de Narratiq EditorialAI România · FEUR-1 Architecture Specification v1.0", { color: GRAY, size: 14, italics: true, align: AlignmentType.CENTER }),
  ];

  const d = new Document({
    sections: [{ properties: { page: { margin: { top: 900, bottom: 900, left: 1100, right: 1100 } } }, children }],
    styles: { default: { document: { run: { font: "Calibri", size: 18, color: DARK } } } },
  });
  const blob = await Packer.toBlob(d);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `FEUR-${r.title.replace(/\s+/g,"-").slice(0,30)}-${new Date().toISOString().slice(0,10)}.docx`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// SAVE REPORT TO BACKEND
// ─────────────────────────────────────────────────────────────────────────────
async function saveReportToServer(reportData, token) {
  if (!token) return { ok: false };
  try {
    const res = await fetch(`${API}/api/reports`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        tier:         reportData.tierId,
        title:        reportData.title,
        author:       reportData.author,
        genre_id:     reportData.genreId,
        genre_label:  reportData.genreLabel,
        feur_score:   reportData.feur,
        uc_score:     reportData.ucScore,
        genre_score:  reportData.genreScore10,
        rml_score:    reportData.rmlScore100,
        verdict:      reportData.verdict,
        confidence:   reportData.confidence,
        scores_json: JSON.stringify({
          uc:              reportData.uc,
          genre:           reportData.genre,
          rml:             reportData.rml,
          strengths:       reportData.strengths,
          weaknesses:      reportData.weaknesses,
          recommendations: reportData.recommendations,
          summary:         reportData.summary,
          grammarIssues:   reportData.grammarIssues,
          genreFit:        reportData.genreFit,
          primarySegment:  reportData.primarySegment,
          channels:        reportData.channels,
          logline:         reportData.logline,
          keywords:        reportData.keywords,
          genreVerdict:    reportData.genreVerdict,
        }),
      }),
    });
    return { ok: res.ok };
  } catch {
    return { ok: false };
  }
}

// Download automat per tier după generare
// freemium: PDF (fără salvare pe server) | basic: PDF | restul: PDF + DOCX
async function autoDownload(report) {
  const tier = report.tierId;
  try {
    if (tier === "freemium") {
      await doExportPDF(report); // descarcă dar nu salvează pe server
    } else if (tier === "basic") {
      await doExportPDF(report);
    } else {
      await doExportPDF(report);
      await new Promise(r => setTimeout(r, 800));
      await doExportDOCX(report);
    }
  } catch (err) {
    console.error("Auto-download error:", err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function Screening() {
  const { user, getToken, hasAccess } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const L = lang.screening;

  const [step, setStep]           = useState("form");
  const [title, setTitle]         = useState("");
  const [author, setAuthor]       = useState("");
  const [genreId, setGenreId]     = useState("");
  const [tierId, setTierId]       = useState("freemium");
  const [excerpt, setExcerpt]     = useState("");
  const [file, setFile]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [loadingPct, setLoadingPct] = useState(0);
  const [report, setReport]       = useState(null);
  const [error, setError]         = useState("");
  const [activeTab, setActiveTab] = useState("uc");
  const [exporting, setExporting] = useState(null);
  const [saved, setSaved]         = useState(false);
  const [volume, setVolume]       = useState(2000);
  const fileRef = useRef();

  // ── Tier implicit în funcție de abonament
  useEffect(() => {
    if (!user) { setTierId("freemium"); setVolume(defaultVolume("freemium")); return; }
    const preferred = ["author_pro", "basic", "romanian_market", "international",
                       "premium_comparative", "full_manuscript", "ai_human"];
    const found = preferred.find(t => hasAccess(t));
    const newTier = found || "freemium";
    setTierId(newTier);
    setVolume(defaultVolume(newTier));
  }, [user, hasAccess]);

  // Reset volume când se schimbă tier-ul manual
  const handleTierChange = (newTier) => {
    setTierId(newTier);
    setVolume(defaultVolume(newTier));
  };

  // ── Limita de cuvinte pentru volum selectat
  const maxWords = volume;

  // ── File handler
  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setFile(f);
    const ext = f.name.split(".").pop().toLowerCase();

    if (ext === "txt") {
      const rd = new FileReader();
      rd.onload = ev => setExcerpt(ev.target.result.split(/\s+/).slice(0, maxWords).join(" "));
      rd.readAsText(f);
    } else if (ext === "docx" || ext === "doc") {
      const mammoth = await import("mammoth");
      const ab = await f.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer: ab });
      setExcerpt(result.value.split(/\s+/).slice(0, maxWords).join(" "));
    } else if (ext === "pdf") {
      const pdfjsLib = await import("pdfjs-dist");
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      const ab = await f.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: ab }).promise;
      let text = "";
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        text += content.items.map(item => item.str).join(" ") + "\n";
      }
      setExcerpt(text.split(/\s+/).slice(0, maxWords).join(" "));
    } else {
      setError("Fișierele .odt nu sunt suportate. Convertește în .docx sau .txt.");
    }
  }, [maxWords]);

  const handleFileInput = useCallback(e => handleFile(e.target.files[0]), [handleFile]);
  const handleDrop = useCallback(e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }, [handleFile]);

  // ── Submit
  const handleSubmit = async () => {
    if (!title || !genreId || !excerpt) { setError(L.errRequired); return; }
    if (tierId !== "freemium" && !user)   { setError(L.errAuth);     return; }
    if (tierId !== "freemium" && !hasAccess(tierId)) { setError(L.errNoSub); return; }

    setError(""); setLoading(true); setStep("loading"); setLoadingPct(0); setSaved(false);
    let mi = 0; setLoadingMsg(L.loadingMsgs[0]);
    const iv = setInterval(() => {
      mi = Math.min(mi + 1, L.loadingMsgs.length - 1);
      setLoadingMsg(L.loadingMsgs[mi]);
      setLoadingPct(Math.round((mi / (L.loadingMsgs.length - 1)) * 85));
    }, 2000);

    try {
      const isFreemium = tierId === "freemium";
      const prompt = isFreemium
        ? buildFreemiumPrompt(title, author, genreId, excerpt)
        : buildPrompt(title, author, genreId, excerpt, lang._meta?.reportLanguage || "Romanian");

      const endpoint = isFreemium ? `${API}/api/analyze/freemium` : `${API}/api/analyze`;
      const token = getToken();

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: [{ role: "user", content: prompt }],
          tier: tierId,
          volume,
        }),
      });

      clearInterval(iv); setLoadingPct(95);
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 429) { setError(L.freemiumLimit); setStep("form"); return; }
        throw new Error(data.error || "API error");
      }

      const raw = data.content?.map(b => b.text || "").join("") || "";
      let p = {};
      try {
        const m = raw.replace(/```json|```/g, "").match(/\{[\s\S]*\}/);
        if (m) p = JSON.parse(m[0]);
      } catch { p = {}; }

      const genreLabel = GENRES.find(g => g.id === genreId)?.label || genreId;

      if (isFreemium) {
        setReport({
          isFreemium: true,
          tierId, title, author, genreId, genreLabel,
          hookScore:        p.hook || 5,
          genreConfirmed:   p.genre_confirmed || genreLabel,
          strength:         p.strength || "",
          issue:            p.issue || "",
          recommendedTier:  p.recommended_tier || "author_pro",
          summary:          p.summary || "",
          timestamp:        new Date().toLocaleString("ro-RO"),
        });
      } else {
        const { uc, genre, rml, ucScore, genreScore10, rmlScore100, feur } =
          computeScores(p.uc || {}, p.genre || {}, p.rml || {}, genreId);
        const verdict  = getVerdict(feur);
        const bigPub   = typeof p.big_publisher_fit  === "number" ? Math.min(100, Math.max(0, p.big_publisher_fit))  : Math.round(rmlScore100 * 0.9);
        const indiePub = typeof p.indie_publisher_fit === "number" ? Math.min(100, Math.max(0, p.indie_publisher_fit)) : Math.min(100, Math.round(rmlScore100 * 1.05));
        const gmod     = GENRE_MODULES[genreId];

        const fullReport = {
          isFreemium: false,
          tierId, title, author, genreId, genreLabel,
          uc, genre, rml, ucScore, genreScore10, rmlScore100, bigPub, indiePub, feur, verdict, gmod,
          genreVerdict:   p.genre_verdict   || "",
          grammarIssues:  p.grammar_issues  || "",
          genreFit:       p.genre_fit       || "",
          strengths:      Array.isArray(p.strengths)      ? p.strengths      : [],
          weaknesses:     Array.isArray(p.weaknesses)     ? p.weaknesses     : [],
          recommendations:Array.isArray(p.recommendations)? p.recommendations: [],
          summary:        p.summary         || "",
          confidence:     typeof p.confidence === "number" ? p.confidence    : 70,
          primarySegment: p.primary_segment || "",
          channels:       Array.isArray(p.distribution_channels) ? p.distribution_channels : [],
          formatRec:      p.format_recommendation || "",
          logline:        p.marketing_logline     || "",
          keywords:       Array.isArray(p.marketing_keywords) ? p.marketing_keywords : [],
          timestamp:      new Date().toLocaleString("ro-RO"),
        };

        setReport(fullReport);

        // Salvare automată dacă utilizatorul e autentificat
        if (user && token) {
          saveReportToServer(fullReport, token).then(result => {
            if (result.ok) setSaved(true);
          });
        }

        // Download automat per tier
        autoDownload(fullReport);
      }

      setLoadingPct(100);
      setTimeout(() => { setStep("report"); setActiveTab("uc"); }, 300);

    } catch (err) {
      clearInterval(iv);
      setError(L.errAPI);
      setStep("form");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep("form"); setTitle(""); setAuthor(""); setGenreId("");
    setExcerpt(""); setFile(null); setReport(null); setError(""); setSaved(false);
  };

  const exportPDF  = async () => { setExporting("pdf");  try { await doExportPDF(report);  } catch { alert(L.errPDF);  } finally { setExporting(null); } };
  const exportDOCX = async () => { setExporting("docx"); try { await doExportDOCX(report); } catch { alert(L.errDOCX); } finally { setExporting(null); } };

  const wordCount = excerpt ? excerpt.split(/\s+/).filter(Boolean).length : 0;

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: LOADING
  // ────────────────────────────────────────────────────────────────────────────
  if (step === "loading") return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <Navbar />
      <div style={{ minHeight: 380, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20, padding: "3rem 1rem" }}>
        <div style={{ width: 52, height: 52, border: "3px solid #e5e7eb", borderTopColor: "#1d4ed8", borderRadius: "50%", animation: "spin 0.85s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ textAlign: "center" }}>
          <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 5, fontFamily: "sans-serif" }}>FEUR Screening</p>
          <p style={{ color: "#6b7280", fontSize: 14, fontFamily: "sans-serif" }}>{loadingMsg}</p>
        </div>
        <div style={{ width: 240, height: 5, background: "#e5e7eb", borderRadius: 4 }}>
          <div style={{ height: "100%", width: `${loadingPct}%`, background: "#1d4ed8", borderRadius: 4, transition: "width 0.6s ease" }} />
        </div>
      </div>
    </div>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: FREEMIUM REPORT
  // ────────────────────────────────────────────────────────────────────────────
  if (step === "report" && report?.isFreemium) {
    const hookColor = report.hookScore >= 7 ? "#059669" : report.hookScore >= 5 ? "#d97706" : "#dc2626";
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <Navbar />
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px 16px" }}>
          <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 14, padding: 24, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, fontFamily: "Georgia, serif" }}>{L.reportTitle}</h2>
              <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>{L.freemiumBadge || "Analiză gratuită"}</span>
            </div>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 20px" }}>{report.title}{report.author ? ` · ${report.author}` : ""} · {report.genreConfirmed}</p>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 20 }}>
              <div style={{ textAlign: "center" }}>
                <p style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>Hook Score</p>
                <svg width="80" height="80" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                  <circle cx="50" cy="50" r="40" fill="none" stroke={hookColor} strokeWidth="8"
                    strokeDasharray={`${(report.hookScore / 10) * 2 * Math.PI * 40} ${2 * Math.PI * 40}`}
                    strokeLinecap="round" transform="rotate(-90 50 50)" />
                  <text x="50" y="46" textAnchor="middle" fontSize="20" fontWeight="700" fill={hookColor}>{report.hookScore}</text>
                  <text x="50" y="60" textAnchor="middle" fontSize="9" fill="#9ca3af">/ 10</text>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                {report.summary && <p style={{ fontSize: 14, lineHeight: 1.7, color: "#374151", fontFamily: "Georgia, serif", margin: "0 0 12px" }}>{report.summary}</p>}
                {report.strength && <p style={{ fontSize: 13, color: "#065f46", background: "#f0fdf4", border: "0.5px solid #bbf7d0", borderRadius: 8, padding: "8px 12px", margin: "0 0 6px" }}>✓ {report.strength}</p>}
                {report.issue    && <p style={{ fontSize: 13, color: "#9a3412", background: "#fff7ed", border: "0.5px solid #fed7aa", borderRadius: 8, padding: "8px 12px", margin: 0 }}>⚠ {report.issue}</p>}
              </div>
            </div>

            {/* Upsell */}
            <div style={{ background: "#eff6ff", border: "0.5px solid #bfdbfe", borderRadius: 12, padding: 18, textAlign: "center" }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: "#1e40af", margin: "0 0 6px" }}>Raport FEUR complet</p>
              <p style={{ fontSize: 13, color: "#3b82f6", margin: "0 0 14px", lineHeight: 1.6 }}>
                UC · 9 motoare editoriale · Modul de gen · Romanian Market Layer<br />
                Strengths, weaknesses, recomandări · Logline · Export PDF/DOCX
              </p>
              <button
                onClick={() => navigate("/pricing")}
                style={{ padding: "10px 28px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              >
                Vezi pachetele →
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={reset}
              style={{ flex: 1, padding: "11px 0", background: "#fff", color: "#374151", border: "0.5px solid #d1d5db", borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: "pointer" }}>
              {L.btnNew}
            </button>
          </div>
          <p style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", marginTop: 12 }}>{L.freemiumDesc}</p>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: FULL REPORT (paid tiers)
  // ────────────────────────────────────────────────────────────────────────────
  if (step === "report" && report && !report.isFreemium) {
    const v    = VERDICTS[report.verdict];
    const rmlV = getRmlVerdict(report.rmlScore100);
    const gmod = report.gmod;
    const TABS = [
      { id: "uc",    label: "Universal Core" },
      { id: "genre", label: gmod.label },
      { id: "market",label: "Piața Română" },
    ];
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <Navbar />
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "1.5rem 1rem" }}>

          {/* Header raport */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, fontFamily: "Georgia, serif" }}>Raport FEUR</h2>
              <span style={{ fontSize: 11, background: "#dbeafe", color: "#1e40af", padding: "2px 7px", borderRadius: 10, fontWeight: 500 }}>v1.0</span>
              {saved && <span style={{ fontSize: 11, background: "#d1fae5", color: "#065f46", padding: "2px 7px", borderRadius: 10 }}>✓ {L.savedOk}</span>}
            </div>
            <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>
              {report.title}{report.author ? ` · ${report.author}` : ""} · {report.genreLabel} · {report.timestamp}
            </p>
          </div>

          {/* Score box principal */}
          <div style={{ background: "#f9fafb", border: "0.5px solid #e5e7eb", borderRadius: 12, padding: "16px 18px", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 12 }}>
              <ScoreGauge score={report.feur} max={100} size={96} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, margin: "0 0 5px" }}>FEUR Base Score</p>
                <div style={{ display: "inline-block", background: v.bg, color: v.color, fontWeight: 700, fontSize: 13, padding: "3px 12px", borderRadius: 16, marginBottom: 7 }}>{v.label}</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>UC <strong style={{ color: "#374151" }}>{report.ucScore}/50</strong></span>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Gen <strong style={{ color: "#374151" }}>{report.genreScore10}/10</strong></span>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Încredere <strong style={{ color: "#374151" }}>{report.confidence}%</strong></span>
                </div>
              </div>
            </div>
            <div style={{ borderTop: "0.5px solid #e5e7eb", paddingTop: 12, display: "flex", alignItems: "center", gap: 14 }}>
              <ScoreGauge score={report.rmlScore100} max={100} size={72} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, margin: "0 0 4px" }}>Romanian Market Fit</p>
                <div style={{ display: "inline-block", background: rmlV.bg || "#d1fae5", color: rmlV.color || "#059669", fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 14, marginBottom: 5 }}>{rmlV.label}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>Editură mare <strong style={{ color: "#374151" }}>{report.bigPub}/100</strong></span>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>Indie <strong style={{ color: "#374151" }}>{report.indiePub}/100</strong></span>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          {report.summary && (
            <p style={{ fontSize: 14, lineHeight: 1.75, color: "#374151", fontFamily: "Georgia, serif", marginBottom: 14, padding: "0 2px" }}>
              {report.summary}
            </p>
          )}

          {/* Strengths / Weaknesses */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div style={{ background: "#f0fdf4", border: "0.5px solid #bbf7d0", borderRadius: 10, padding: 14 }}>
              <p style={{ fontWeight: 600, fontSize: 13, color: "#065f46", margin: "0 0 7px" }}>✓ Puncte forte</p>
              {report.strengths.length
                ? report.strengths.map((s,i) => <p key={i} style={{ fontSize: 13, color: "#065f46", margin: "0 0 4px" }}>· {s}</p>)
                : <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>–</p>}
            </div>
            <div style={{ background: "#fff7ed", border: "0.5px solid #fed7aa", borderRadius: 10, padding: 14 }}>
              <p style={{ fontWeight: 600, fontSize: 13, color: "#9a3412", margin: "0 0 7px" }}>⚠ Probleme detectate</p>
              {report.weaknesses.length
                ? report.weaknesses.map((w,i) => <p key={i} style={{ fontSize: 13, color: "#9a3412", margin: "0 0 4px" }}>· {w}</p>)
                : <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>–</p>}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ border: "0.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
            <div style={{ display: "flex", borderBottom: "0.5px solid #e5e7eb" }}>
              {TABS.map((tab, i) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  style={{ flex: 1, padding: "10px 6px", fontSize: 12, fontWeight: activeTab === tab.id ? 600 : 400, background: activeTab === tab.id ? "#fff" : "#f9fafb", border: "none", borderRight: i < TABS.length - 1 ? "0.5px solid #e5e7eb" : "none", cursor: "pointer", color: activeTab === tab.id ? "#1d4ed8" : "#6b7280", fontFamily: "sans-serif" }}>
                  {tab.label}
                </button>
              ))}
            </div>
            <div style={{ padding: "16px 16px 8px" }}>

              {activeTab === "uc" && (
                <>
                  <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 14px" }}>Pondere în FEUR Base Score: 50% · Contribuție: {report.ucScore}/50</p>
                  {UC_ENGINES.map(e => <EngineRow key={e.id} engine={e} score={report.uc[e.id] || 5} />)}
                  <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div style={{ border: "0.5px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
                      <p style={{ fontWeight: 600, fontSize: 12, margin: "0 0 5px" }}>Gramatică & diacritice</p>
                      <p style={{ fontSize: 12, color: "#6b7280", margin: 0, lineHeight: 1.55 }}>{report.grammarIssues || "–"}</p>
                    </div>
                    <div style={{ border: "0.5px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
                      <p style={{ fontWeight: 600, fontSize: 12, margin: "0 0 5px" }}>Compatibilitate · {report.genreLabel}</p>
                      <p style={{ fontSize: 12, color: "#6b7280", margin: 0, lineHeight: 1.55 }}>{report.genreFit || "–"}</p>
                    </div>
                  </div>
                </>
              )}

              {activeTab === "genre" && (
                <>
                  <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 4px" }}>Pondere în FEUR Base Score: 40% · Scor modul: {report.genreScore10}/10</p>
                  {report.genreVerdict && <div style={{ display: "inline-block", background: "#eff6ff", color: "#1e40af", fontSize: 12, padding: "3px 10px", borderRadius: 8, marginBottom: 10 }}>{report.genreVerdict}</div>}
                  <p style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", margin: "0 0 14px", lineHeight: 1.6 }}>„{gmod.question}"</p>
                  {gmod.engines.map(e => <EngineRow key={e.id} engine={e} score={report.genre[e.id] || 5} />)}
                </>
              )}

              {activeTab === "market" && (
                <>
                  <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 4px" }}>Scor separat față de FEUR Base · Nu modifică scorul editorial de bază</p>
                  <div style={{ display: "inline-block", background: rmlV.bg || "#d1fae5", color: rmlV.color || "#059669", fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 8, marginBottom: 14 }}>{rmlV.label}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
                    <ScoreCard label="Romanian Market Fit" score={report.rmlScore100} max={100} />
                    <ScoreCard label="Editură mare" score={report.bigPub} max={100} sub={report.bigPub >= 80 ? "Recomandat" : report.bigPub >= 60 ? "Posibil" : "Risc ridicat"} />
                    <ScoreCard label="Indie / Self-pub" score={report.indiePub} max={100} sub={report.indiePub >= 80 ? "Foarte potrivit" : report.indiePub >= 60 ? "Potrivit" : "Cu strategie"} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 14px" }}>Romanian Market Layer — 9 motoare</p>
                  {RML_ENGINES.map(e => <EngineRow key={e.id} engine={e} score={report.rml[e.id] || 5} />)}
                  {report.primarySegment && (
                    <div style={{ marginTop: 12, background: "#f0f9ff", border: "0.5px solid #bae6fd", borderRadius: 8, padding: 12 }}>
                      <p style={{ fontWeight: 600, fontSize: 12, color: "#0369a1", margin: "0 0 4px" }}>Cititor principal român</p>
                      <p style={{ fontSize: 13, color: "#0c4a6e", margin: 0, lineHeight: 1.5 }}>{report.primarySegment}</p>
                    </div>
                  )}
                  {report.channels.length > 0 && (
                    <div style={{ marginTop: 10, border: "0.5px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
                      <p style={{ fontWeight: 600, fontSize: 12, margin: "0 0 6px" }}>Canale recomandate</p>
                      {report.channels.map((c,i) => <p key={i} style={{ fontSize: 12, color: "#374151", margin: "0 0 3px" }}>· {c}</p>)}
                    </div>
                  )}
                  {report.logline && (
                    <div style={{ marginTop: 10, border: "0.5px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
                      <p style={{ fontWeight: 600, fontSize: 12, margin: "0 0 5px" }}>Logline piața românească</p>
                      <p style={{ fontSize: 13, color: "#374151", fontStyle: "italic", margin: "0 0 8px", lineHeight: 1.55, fontFamily: "Georgia, serif" }}>{report.logline}</p>
                      {report.keywords.length > 0 && <div>{report.keywords.map((k,i) => <Tag key={i} text={k} color="#1e40af" bg="#dbeafe" />)}</div>}
                    </div>
                  )}
                </>
              )}

            </div>
          </div>

          {/* Recomandări */}
          <div style={{ border: "0.5px solid #dbeafe", background: "#eff6ff", borderRadius: 10, padding: 14, marginBottom: 20 }}>
            <p style={{ fontWeight: 600, fontSize: 13, color: "#1e40af", margin: "0 0 8px" }}>Recomandări editoriale FEUR</p>
            {report.recommendations.length
              ? report.recommendations.map((rec,i) => <p key={i} style={{ fontSize: 13, color: "#1e3a8a", margin: "0 0 5px", lineHeight: 1.5 }}>{i+1}. {rec}</p>)
              : <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>–</p>}
          </div>

          {/* Export buttons */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
            <button onClick={exportPDF} disabled={!!exporting}
              style={{ padding: "11px 0", background: exporting === "pdf" ? "#93c5fd" : "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: exporting ? "wait" : "pointer" }}>
              {exporting === "pdf" ? "Se generează…" : "⬇ Export PDF"}
            </button>
            <button onClick={exportDOCX} disabled={!!exporting}
              style={{ padding: "11px 0", background: "#eff6ff", color: "#1e40af", border: "0.5px solid #bfdbfe", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: exporting ? "wait" : "pointer" }}>
              {exporting === "docx" ? "Se generează…" : "⬇ Export DOCX"}
            </button>
            <button onClick={reset}
              style={{ padding: "11px 0", background: "transparent", color: "#374151", border: "0.5px solid #d1d5db", borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: "pointer" }}>
              {L.btnNew}
            </button>
          </div>
          <p style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", marginTop: 10 }}>
            FEUR-1 Architecture Specification v1.0 · Narratiq EditorialAI România · Raport AI. Nu înlocuiește editorul uman.
          </p>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: FORM
  // ────────────────────────────────────────────────────────────────────────────
  const gmod = genreId ? GENRE_MODULES[genreId] : null;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <Navbar />
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "24px 16px" }}>

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 5 }}>
            <h1 style={{ fontSize: 21, fontWeight: 700, margin: 0, fontFamily: "Georgia, serif" }}>{L.title}</h1>
            <span style={{ fontSize: 11, background: "#dbeafe", color: "#1e40af", padding: "2px 8px", borderRadius: 10, fontWeight: 500 }}>{L.versionBadge}</span>
          </div>
          <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>{L.sub}</p>
        </div>

        {/* Tier selector */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>{L.labelTier}</label>
          <select value={tierId} onChange={e => handleTierChange(e.target.value)}
            style={{ width: "100%", padding: "9px 12px", border: "0.5px solid #d1d5db", borderRadius: 8, fontSize: 14, background: "#fff" }}>
            {TIERS.map(t => (
              <option key={t.id} value={t.id} disabled={t.id !== "freemium" && !hasAccess(t.id)}>
                {t.label} — max {t.words.toLocaleString()} cuvinte{t.id !== "freemium" && !hasAccess(t.id) ? " 🔒" : ""}
              </option>
            ))}
          </select>
          {tierId === "freemium" && (
            <p style={{ fontSize: 12, color: "#d97706", margin: "5px 0 0" }}>{L.freemiumDesc}</p>
          )}
          {tierId !== "freemium" && !hasAccess(tierId) && (
            <p style={{ fontSize: 12, color: "#dc2626", margin: "5px 0 0" }}>
              Nu ai acces la acest tier. <button onClick={() => navigate("/pricing")} style={{ color: "#1d4ed8", background: "none", border: "none", cursor: "pointer", fontSize: 12, textDecoration: "underline", padding: 0 }}>Vezi prețuri →</button>
            </p>
          )}
        </div>

        {/* Selector volum — apare doar dacă tier-ul are mai multe volume */}
        {(TIER_VOLUMES[tierId] || []).length > 1 && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Volum analiză</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(TIER_VOLUMES[tierId] || []).map(vol => (
                <button
                  key={vol}
                  onClick={() => setVolume(vol)}
                  style={{
                    padding: "7px 16px",
                    borderRadius: 8,
                    border: volume === vol ? "1.5px solid #1d4ed8" : "0.5px solid #d1d5db",
                    background: volume === vol ? "#eff6ff" : "#fff",
                    color: volume === vol ? "#1d4ed8" : "#374151",
                    fontSize: 13,
                    fontWeight: volume === vol ? 600 : 400,
                    cursor: "pointer",
                  }}
                >
                  {vol >= 1000 ? `${(vol/1000).toFixed(0)}k` : vol} cuvinte
                </button>
              ))}
            </div>
            {volume >= 45000 && (
              <p style={{ fontSize: 12, color: "#6b7280", margin: "5px 0 0" }}>
                Manuscris extins — analiza poate dura 2-3 minute
              </p>
            )}
          </div>
        )}

        {/* Titlu */}
        <div style={{ marginBottom: 13 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>{L.labelTitleReq}</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder={L.labelTitlePh}
            style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: "0.5px solid #d1d5db", borderRadius: 8, fontSize: 14 }} />
        </div>

        {/* Autor */}
        <div style={{ marginBottom: 13 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>{L.labelAuthor}</label>
          <input value={author} onChange={e => setAuthor(e.target.value)} placeholder={L.labelAuthorPh}
            style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: "0.5px solid #d1d5db", borderRadius: 8, fontSize: 14 }} />
        </div>

        {/* Gen */}
        <div style={{ marginBottom: 13 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>{L.labelGenre}</label>
          <select value={genreId} onChange={e => setGenreId(e.target.value)}
            style={{ width: "100%", padding: "9px 12px", border: "0.5px solid #d1d5db", borderRadius: 8, fontSize: 14, background: "#fff" }}>
            <option value="">{L.labelGenrePh}</option>
            {GENRES.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
          {gmod && (
            <div style={{ marginTop: 8, background: "#f0f9ff", border: "0.5px solid #bae6fd", borderRadius: 8, padding: "10px 12px" }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: "#0369a1", margin: "0 0 2px" }}>{L.genreActivated(gmod.label)}</p>
              <p style={{ fontSize: 11, color: "#0c4a6e", margin: 0 }}>
                {L.genreEngines(gmod.engines.length, gmod.engines.map(e => e.label).join(" · "))}
              </p>
            </div>
          )}
        </div>

        {/* Text / upload */}
        <div style={{ marginBottom: 13 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>{L.labelText}</label>
          <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 7px" }}>{L.labelTextHint}</p>
          <div
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileRef.current?.click()}
            style={{ border: "1.5px dashed #d1d5db", borderRadius: 10, padding: 16, textAlign: "center", cursor: "pointer", marginBottom: 8, background: "#fafafa" }}
          >
            <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
              {file ? `📄 ${file.name}` : L.labelDropzone}
            </p>
            <input ref={fileRef} type="file" accept=".txt,.doc,.docx,.pdf" style={{ display: "none" }} onChange={handleFileInput} />
          </div>
          <textarea value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder={L.labelPastePh} rows={7}
            style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", border: "0.5px solid #d1d5db", borderRadius: 8, fontSize: 13, fontFamily: "Georgia, serif", lineHeight: 1.7, resize: "vertical" }} />
          <p style={{ fontSize: 12, color: wordCount > maxWords * 0.9 ? "#d97706" : "#9ca3af", margin: "3px 0 0" }}>
            {excerpt ? `${wordCount.toLocaleString()} / ${maxWords.toLocaleString()} cuvinte` : L.labelWordMin}
          </p>
        </div>

        {error && (
          <div style={{ background: "#fef2f2", border: "0.5px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
            <p style={{ fontSize: 13, color: "#b91c1c", margin: 0 }}>{error}</p>
          </div>
        )}

        <button onClick={handleSubmit} disabled={loading}
          style={{ width: "100%", padding: "12px 0", background: loading ? "#93c5fd" : "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? L.btnSubmitting : L.btnSubmit}
        </button>

        {/* KPI bar */}
        <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {[["9","UC motoare"],["12","Module gen"],["9","RML motoare"],["6","Verdicturi"]].map(([v,s]) => (
            <div key={s} style={{ textAlign: "center", border: "0.5px solid #e5e7eb", borderRadius: 8, padding: "9px 4px" }}>
              <p style={{ fontSize: 17, fontWeight: 700, margin: "0 0 1px", color: "#1d4ed8" }}>{v}</p>
              <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>{s}</p>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", marginTop: 14 }}>
          FEUR-1 Architecture Specification v1.0 · Narratiq EditorialAI România · Textul nu este stocat.
        </p>
      </div>
    </div>
  );
}
